import React, { useEffect, useState } from 'react';
import Payment from 'components/Payment';
import hooks from 'hooks';
import { PaymentTabEnum, SIZE_ICON, tabOfSettings } from 'consts/data';
import classNames from 'classnames';
import ListPackage from 'components/Payment/ListPackage';
import Transaction from 'components/Payment/Transaction';
import { useQueryParam } from 'hooks/useQueryParam';
import useGetPaymentMethodSelected from 'hooks/payment/useGetPaymentMethodSelected';
import { useHistory } from 'react-router';
import queryString from 'query-string';
import { usePromptBlocker } from 'providers/PromptBlockerProvider';
import { useFormikContext } from 'formik';
import { AppRoutes } from 'consts/routesPath';
import NewDesign from 'components/NewDesign';
import { useCheckingBillingInformationProvider } from 'providers/CheckingBillingInformationProvider';
import { CommonIcons } from 'components/Icons';
import Loading from 'components/Loading';
import useToggleDialog from 'hooks/useToggleDialog';
import DialogUpdateProfile from 'components/Payment/Dialog/DialogUpdateProfile';
import Profile from './Profile';
import PaymentProviderProvider from 'providers/PaymentProvider';
import getUser from 'redux/selector/getUser';
import { Redirect } from 'react-router/cjs/react-router.min';

const { useGetListCard, useGetListHisCharge, useGetListPackage } = hooks;

const PaymentTab = ({ skipCheckDirty, noChangeTabURL, disableAutoRecharge }) => {
  //! State
  const {
    settings: { enableCreditCard },
  } = getUser();
  const history = useHistory();
  const params = useQueryParam();
  const { paymentTab } = params || {};
  const [tab, setTab] = useState(paymentTab || PaymentTabEnum.buyPackage);
  const { handleDirtyCheck } = usePromptBlocker();
  const { dirty, resetForm } = useFormikContext();

  const { isFetchingListCard } = useGetPaymentMethodSelected();
  useGetListCard();
  useGetListHisCharge();
  useGetListPackage();

  //! Function
  //* Hide btn Update
  useEffect(() => {
    const btnUpdate = document.querySelector('.btn-update-setting');
    if (btnUpdate) {
      if (tab === PaymentTabEnum.paymentMethod) {
        btnUpdate.classList.remove('d-none');
      } else {
        btnUpdate.classList.add('d-none');
      }
    }
  }, [tab]);

  const onClickTab = (eachTab) => () => {
    if (skipCheckDirty) {
      if (!noChangeTabURL) {
        history.replace({
          pathname: AppRoutes.Settings,
          search: queryString.stringify({ ...params, paymentTab: eachTab }),
        });
      }

      setTab(eachTab);
      return;
    }

    handleDirtyCheck({
      isDirty: dirty,
      onPass: () => {
        resetForm();
        history.replace({
          pathname: history.location.pathname,
          search: queryString.stringify({ ...params, paymentTab: eachTab }),
        });
        setTab(eachTab);
      },
    });
  };

  //! Render
  const tabs = [
    {
      label: 'Buy package',
      value: PaymentTabEnum.buyPackage,
    },
    enableCreditCard && {
      label: 'Payment method',
      value: PaymentTabEnum.paymentMethod,
    },
    {
      label: 'Transactions',
      value: PaymentTabEnum.transactions,
    },
  ].filter((el) => !!el);

  const renderTab = () => {
    return (
      <div className='nav-send'>
        {tabs.map((eachTab) => {
          return (
            <div
              key={eachTab.value}
              onClick={onClickTab(eachTab.value)}
              className={classNames('is-hover', { 'nav-send__active': eachTab.value === tab })}
            >
              {eachTab.label}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPaymentByTab = () => {
    if (tab === PaymentTabEnum.buyPackage) {
      return (
        <ListPackage
          isFetchingCard={isFetchingListCard}
          onClickToRedirectPaymentMethod={onClickTab(PaymentTabEnum.paymentMethod)}
        />
      );
    }

    if (tab === PaymentTabEnum.paymentMethod) {
      if (enableCreditCard) {
        return <Payment disableAutoRecharge={disableAutoRecharge} />;
      }

      onClickTab(PaymentTabEnum.buyPackage)();
      return <Loading />;
    }

    return <Transaction />;
  };

  return (
    <div className='payment-container'>
      {renderTab()}
      {renderPaymentByTab()}
    </div>
  );
};

const PaymentWithCheckingBilling = (props) => {
  const history = useHistory();
  const {
    isInProgress,
    noNeedUpdate,
    isVerified,
    refreshBillingInformation,
    checkingBillingInformation,
  } = useCheckingBillingInformationProvider();
  const [openUpdateBillingInfo, toggleUpdateBillingInfo, shouldRenderUpdateBillingInfo] =
    useToggleDialog();

  if (checkingBillingInformation) {
    return <Loading className='mt-1' />;
  }

  if (isInProgress) {
    return (
      <div className='component:PaymentWithCheckingBilling mb-2'>
        <Profile hidePersonalInformation />
      </div>
    );
  }

  if (isVerified || noNeedUpdate) {
    return <PaymentTab {...props} />;
  }

  return (
    <NewDesign.Alert color='danger' className='mb-0'>
      {shouldRenderUpdateBillingInfo && (
        <DialogUpdateProfile
          isOpen={openUpdateBillingInfo}
          toggle={toggleUpdateBillingInfo}
          onSuccess={() => {
            refreshBillingInformation();
          }}
        />
      )}

      <div className='d-flex gap-16px mobile:flex-column'>
        <CommonIcons.AiOutlineException size={44} />
        <div>
          <NewDesign.Typography component='h4' style={{ marginBottom: 4 }}>
            Your account does not have billing information!
          </NewDesign.Typography>

          <NewDesign.Typography component='p' style={{ marginLeft: 1, marginBottom: 4 }}>
            Please update your billing information before accessing this feature. If you have
            finished updating your billing information but the problem still persists, please
            contact to our supporters.
          </NewDesign.Typography>

          <NewDesign.Typography component='p' style={{ marginBottom: 8 }}>
            Thanks!
          </NewDesign.Typography>

          <NewDesign.Button
            color='danger'
            onClick={() => {
              props.allowUpdateOnPopup
                ? toggleUpdateBillingInfo()
                : history.push(`${AppRoutes.Settings}?tab=${tabOfSettings.profile}`);
            }}
          >
            <CommonIcons.Edit size={SIZE_ICON} /> Update billing infomation now
          </NewDesign.Button>
        </div>
      </div>
    </NewDesign.Alert>
  );
};

export default (props) => {
  return (
    <PaymentProviderProvider shouldOpenByPopup={props.shouldOpenByPopup}>
      <PaymentWithCheckingBilling {...props} />
    </PaymentProviderProvider>
  );
};
